import React, { useState, useMemo } from "react";
import Loader from "@components/Loader";
import ProductForm from "@components/catalog/ProductForm";
import { useParams, useNavigate } from "react-router-dom";
import {
  useProductQuery,
  useSaveProductMutation,
  useCreateProductMutation
} from "@queries/catalog";
import "@components/css/EditCategory.css";

export default function ProductEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const isNew = !id || id === "new";

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch
  } = useProductQuery(isNew ? null : id);

  const saveMutation = useSaveProductMutation();
  const createMutation = useCreateProductMutation();

  const [fieldErrors, setFieldErrors] = useState({});
  const [generalError, setGeneralError] = useState("");

  const prod = data?.data;

  const initial = useMemo(() => {
    if (isNew) {
      return {
        id: null,
        model: "",
        sku: "",
        upc: "",
        location: "",
        image: "",
        price: 0,
        quantity: 0,
        minimum: 1,
        sort_order: 0,
        status: 1,
        shipping: 1,
        subtract: 1,
        weight: 0,
        length: 0,
        width: 0,
        height: 0,
        date_available: "",
        pieces: 0,
        item_count: 0,
        is_flagged: 0,
        is_sewing_flagged: 0,
        delivery_withen: "",
        descriptions: {
          ar: {},
          en: {}
        }
      };
    }

    const p = prod || {};

    return {
      ...p,
      is_flagged: p?.is_flagged ?? 0,
      is_sewing_flagged: p?.is_sewing_flagged ?? 0
    };
  }, [isNew, prod]);

  const onSubmit = async (payload) => {
    setFieldErrors({});
    setGeneralError("");

    const normalizedPayload = {
      ...payload,
      is_flagged:
        payload?.is_flagged === true
          ? 1
          : payload?.is_flagged === false
          ? 0
          : Number(payload?.is_flagged ?? 0),
      is_sewing_flagged:
        payload?.is_sewing_flagged === true
          ? 1
          : payload?.is_sewing_flagged === false
          ? 0
          : Number(payload?.is_sewing_flagged ?? 0)
    };

    try {
      if (isNew) {
        await createMutation.mutateAsync(normalizedPayload);
        navigate("/catalog/products");
      } else {
        await saveMutation.mutateAsync({ id, payload: normalizedPayload });
        await refetch();
      }
    } catch (err) {
      const res = err?.response;
      const status = res?.status;

      if (res?.data) {
        const d = res.data;
        if (d.errors) {
          setFieldErrors(d.errors);
        }
        if (d.message) {
          setGeneralError(d.message);
        } else if (status >= 500) {
          setGeneralError("Server error, please try again.");
        } else {
          setGeneralError("Failed to save product.");
        }
      } else {
        setGeneralError("Failed to save product.");
      }
    }
  };

  const loading =
    (!isNew && (isLoading || isFetching)) ||
    saveMutation.isPending ||
    createMutation.isPending;

  return (
    <div className="oc-content">
      {loading && <Loader />}

      <div className="oc-header">
        <h1>{isNew ? "Create Product" : "Edit Product"}</h1>
        <nav className="oc-bc">
          <span>Home</span>
          <span className="sep">›</span>
          <span>Catalog</span>
          <span className="sep">›</span>
          <span className="link" onClick={() => navigate("/catalog/products")}>
            Products
          </span>
          <span className="sep">›</span>
          <span>{isNew ? "New" : id}</span>
        </nav>
      </div>

      {isError && !isNew && (
        <div className="oc-card" style={{ color: "#c00" }}>
          Error: {error?.message || "Failed to load product."}
        </div>
      )}

      <div className="oc-card">
        {(isNew || prod) && (
          <ProductForm
            initial={initial}
            onSubmit={onSubmit}
            onCancel={() => navigate("/catalog/products")}
            errors={fieldErrors}
            generalError={generalError}
          />
        )}
      </div>
    </div>
  );
}

